/**
 * The confirm dialog (D179): the product's one destructive gate as chrome —
 * a sentence, and two buttons, the irreversible one in the danger color.
 *
 * @remarks
 * Built once, where every caller can reach it: the title screen's BEGIN
 * AGAIN opens it; a `restart` typed in play asks the same question through
 * the engine's confirm question and the input line's chips, so the two
 * gates share their words and their color and not their component — a
 * modal over the transcript would be the chrome interrupting the terminal.
 *
 * Pure: every word is a prop, the shell's to say and the design lane's to
 * re-cut. A native `<dialog>` opened as it attaches, the map dialog's
 * pattern, so "open" and "rendered" cannot disagree; Escape is the caller's
 * `onKeep`, because closing a confirm without choosing IS keeping.
 *
 * @packageDocumentation
 */

import { useId } from 'react';

import { showModalWhileMounted } from '../modal-dialog/show-modal-while-mounted.js';

/** Props for {@link ConfirmDialogComponent}. */
export interface ConfirmDialogComponentProps {
	/** The button that keeps things as they are. */
	readonly keepLabel: string;
	/** The player kept it — pressed the keep button, or closed the dialog. */
	readonly onKeep: () => void;
	/** The player went through with it. */
	readonly onProceed: () => void;
	/** The button that does the irreversible thing — drawn in the danger color. */
	readonly proceedLabel: string;
	/** The one sentence: what is about to be thrown away. */
	readonly sentence: string;
}

/**
 * Render the confirm, open.
 *
 * @param props - {@link ConfirmDialogComponentProps}
 * @returns The dialog
 */
export const ConfirmDialogComponent = ({ keepLabel, onKeep, onProceed, proceedLabel, sentence }: ConfirmDialogComponentProps) => {
	const sentenceId = useId();

	return (
		<dialog aria-describedby={sentenceId} aria-label={proceedLabel} className="confirm-dialog" onClose={onKeep} ref={showModalWhileMounted}>
			<div className="confirm-dialog__body">
				<p className="confirm-dialog__sentence" id={sentenceId}>
					{sentence}
				</p>
				<div className="confirm-dialog__choices">
					<button className="confirm-dialog__choice" onClick={onKeep} type="button">
						{keepLabel}
					</button>
					<button className="confirm-dialog__choice confirm-dialog__choice--danger" onClick={onProceed} type="button">
						{proceedLabel}
					</button>
				</div>
			</div>
		</dialog>
	);
};
